import {useState} from "react";
import {useFieldArray, useFormContext, useWatch} from "react-hook-form";
import {ArrowDown, ArrowUp, ChevronDown, ChevronRight, Plus, Swords, Trash2} from "lucide-react";
import {ACTION_TYPES, ActionType} from "@/characters/model/Character.ts";
import {CharacterFormInput} from "@/characters/admin/form/CharacterFormSchema.ts";
import {Button} from "@/components/ui/button.tsx";
import {Badge} from "@/components/ui/badge.tsx";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import TextInputField from "@/components/forms/TextInputField.tsx";
import SelectInputField from "@/components/forms/SelectInputField.tsx";
import MarkdownEditorField from "@/components/markdown/MarkdownEditorField.tsx";
import FormSection from "@/components/forms/FormSection.tsx";

export default function CharacterActionsForm() {
    const {control} = useFormContext<CharacterFormInput>();
    const actionsArray = useFieldArray({control, name: "actions"});
    const actions = useWatch({control, name: "actions"}) ?? [];
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

    const addAction = (type: ActionType) => actionsArray.append({name: "", type, value: ""}, {shouldFocus: false});

    return <FormSection title="Aktionen"
                        description="Merkmale, Aktionen und Reaktionen in ihrer Anzeigereihenfolge."
                        actions={
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button type="button" size="sm"><Plus/> Aktion hinzufügen</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                    {ACTION_TYPES.map(type => (
                        <DropdownMenuItem key={type} onSelect={() => addAction(type)}>{ACTION_LABELS[type]}</DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
        } contentClassName="space-y-3">
            {actionsArray.fields.length === 0 && (
                <div className="grid min-h-48 place-content-center justify-items-center gap-3 rounded-lg border border-dashed text-center text-muted-foreground">
                    <Swords className="size-10"/>
                    <div>
                        <p className="font-medium text-foreground">Noch keine Aktionen</p>
                        <p className="text-sm">Füge ein Merkmal, eine Aktion oder eine Reaktion hinzu.</p>
                    </div>
                </div>
            )}
            {actionsArray.fields.map((arrayField, index) => {
                const action = actions[index];
                const isCollapsed = collapsed[arrayField.id] ?? true;
                return <section key={arrayField.id} className="overflow-hidden rounded-lg border bg-background">
                    <header className="flex flex-wrap items-center gap-2 border-b p-2">
                        <Button type="button" variant="ghost" size="icon-sm"
                                onClick={() => setCollapsed(current => ({...current, [arrayField.id]: !isCollapsed}))}
                                aria-label={isCollapsed ? "Aktion aufklappen" : "Aktion einklappen"}>
                            {isCollapsed ? <ChevronRight/> : <ChevronDown/>}
                        </Button>
                        <Badge variant="outline">{ACTION_LABELS[action?.type ?? "Action"]}</Badge>
                        <span className="min-w-0 grow truncate font-medium">{action?.name || "Neue Aktion"}</span>
                        <Button type="button" variant="ghost" size="icon-sm" title="Nach oben"
                                disabled={index === 0} onClick={() => actionsArray.move(index, index - 1)}><ArrowUp/></Button>
                        <Button type="button" variant="ghost" size="icon-sm" title="Nach unten"
                                disabled={index === actionsArray.fields.length - 1}
                                onClick={() => actionsArray.move(index, index + 1)}><ArrowDown/></Button>
                        <Button type="button" variant="ghost" size="icon-sm" title="Aktion löschen"
                                className="text-destructive hover:text-destructive"
                                onClick={() => actionsArray.remove(index)}><Trash2/></Button>
                    </header>
                    {!isCollapsed && <div className="space-y-4 p-4">
                        <div className="grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(12rem,1fr)]">
                            <TextInputField name={`actions.${index}.name`} title="Name" placeholder="Name der Aktion"/>
                            <SelectInputField name={`actions.${index}.type`} title="Typ" options={ACTION_TYPE_OPTIONS}/>
                        </div>
                        <MarkdownEditorField<CharacterFormInput> name={`actions.${index}.value`}/>
                    </div>}
                </section>;
            })}
    </FormSection>;
}

const ACTION_LABELS: Record<ActionType, string> = {
    Action: "Aktion",
    Reaction: "Reaktion",
    Trait: "Merkmal",
    Legendary: "Legendär",
};

const ACTION_TYPE_OPTIONS = Object.fromEntries(
    ACTION_TYPES.map(type => [type, ACTION_LABELS[type]]),
);
